/**
 * ══ TIỀN LÀ CHUỖI THẬP PHÂN, KHÔNG BAO GIỜ LÀ float ═══════════════════════════════════════════
 * `0.1 + 0.2 !== 0.3`. Với 3.000 dòng bảng kê thì sai số tích luỹ thành tiền thật.
 * CSDL lưu NUMERIC(20,2); tầng TS giữ nguyên chuỗi để không có phép đổi qua float ở giữa.
 * Quy tắc làm tròn: NỬA LÊN (half-up) — đúng thông lệ kế toán VN.
 */
import { Decimal } from 'decimal.js'

// Decimal.clone() tạo MỘT BẢN SAO riêng của decimal.js với cấu hình riêng — không đụng tới cấu
// hình toàn cục của thư viện. `Decimal.set(...)` mutate global lúc import sẽ âm thầm đổi hành vi
// của MỌI nơi khác trong monorepo cũng import decimal.js, kể cả những chỗ không ngờ tới.
const D = Decimal.clone({ precision: 34, rounding: Decimal.ROUND_HALF_UP })

export type Money = string & { readonly __brand: 'Money' }

const DEC_RE = /^-?\d+(\.\d+)?$/

// Số vào có kiểu `number` phải hữu hạn VÀ nằm trong biên an toàn của float (Number.MAX_SAFE_INTEGER).
// Không có biên này, `toMoney(9007199254740993)` (MAX_SAFE_INTEGER + 2) đã bị làm tròn xuống
// `9007199254740992` NGAY TỪ LÚC literal number được tạo — String(v) chỉ in ra số đã sai, lệch
// 1 đơn vị tiền một cách âm thầm. Chặn ở đây, ném đúng lỗi "không phải số hợp lệ" như mọi input rác khác.
const dec = (v: string | number): Decimal => {
  const s = typeof v === 'number'
    ? (Number.isFinite(v) && Math.abs(v) <= Number.MAX_SAFE_INTEGER ? String(v) : 'x')
    : v.trim()
  if (!DEC_RE.test(s)) throw new Error(`Giá trị tiền "${v}" không phải số hợp lệ (cần dạng 45200000 hoặc 45200000.50)`)
  return new D(s)
}

const out = (d: Decimal): Money => d.toFixed(2) as Money

/** Chuẩn hoá về chuỗi 2 chữ số thập phân. Ném lỗi nếu không phải số. */
export const toMoney = (v: string | number): Money => out(dec(v))

export const ZERO: Money = '0.00' as Money

export const addMoney = (a: Money, b: Money): Money => out(dec(a).plus(dec(b)))
export const subMoney = (a: Money, b: Money): Money => out(dec(a).minus(dec(b)))

/** Nhân với hệ số (thuế suất, tỷ lệ). Làm tròn NỬA LÊN về 0 chữ số thập phân — VND không có xu. */
export const mulMoney = (m: Money, factor: string | number): Money =>
  out(dec(m).times(dec(factor)).toDecimalPlaces(0, D.ROUND_HALF_UP))

/**
 * TIỀN THUẾ GTGT TÁCH RA TỪ SỐ TIỀN ĐÃ GỒM THUẾ: `gộp × thuế suất ÷ (100 + thuế suất)`, làm tròn NỬA
 * LÊN về 0 chữ số — chép phép tính phiếu chi có hóa đơn của bản cũ (`Finance.tsx:430`).
 *
 * ⚠ CHIA TRƯỚC KHI LÀM TRÒN, không làm tròn hai lần. `mulMoney` làm tròn ngay sau phép nhân; dùng nó
 *   rồi mới chia là tròn một con số trung gian, và 11.000.000 đ thuế 8 % lệch một đồng.
 */
export const vatOfGross = (gross: Money, ratePct: number): Money => {
  if (!Number.isFinite(ratePct) || ratePct <= 0) return ZERO
  const r = dec(ratePct)
  return out(dec(gross).times(r).dividedBy(r.plus(100)).toDecimalPlaces(0, D.ROUND_HALF_UP))
}

export const sumMoney = (list: readonly Money[]): Money =>
  out(list.reduce((acc, m) => acc.plus(dec(m)), new D(0)))

export const cmpMoney = (a: Money, b: Money): -1 | 0 | 1 => dec(a).cmp(dec(b)) as -1 | 0 | 1

/**
 * ⭐ SỐ THỰC CHI CÒN LẠI của một chứng từ mua = tổng − thuế TNCN giữ lại − đã trả (không âm). MỘT luật cho mọi cửa trả
 *   tiền vào chứng từ mua — Sổ phụ, ĐNTT, đối trừ AR↔AP — ở cả máy chủ (`@rs/ap.payOverError`, `@rs/ar.maxOffset`,
 *   `@rs/pay.createRequest`) lẫn màn. Phần thuế TNCN mình giữ lại nộp thay, không bao giờ trả cho nhà thầu (user 11/08):
 *   tính trần theo `total` là cho lập / chi phần đó rồi mới vỡ ở bước sau (soát Lô 1, 17/10).
 * ⚠ Hoá đơn BÁN không có TNCN — truyền thiếu `whtTotal` thì coi là 0.
 */
export const apRemainToPay = (inv: { total: string; whtTotal?: string | null | undefined; paidAmount: string }): number =>
  Math.max(0, Number(dec(inv.total).minus(dec(inv.whtTotal ?? 0)).minus(dec(inv.paidAmount)).toFixed(2)))

export const isMoney = (v: unknown): v is Money => typeof v === 'string' && DEC_RE.test(v)

/* ══ SỐ TIỀN BẰNG CHỮ ═════════════════════════════════════════════════════════════════════════
 * Chép `docTienVi` (`cashReportDoc.ts:247`) · `docMoneyWords` (`lib/moneyWords.ts`) — hai bản của
 * bản cũ ra CÙNG một câu dưới một nghìn tỷ. Dòng "Viết bằng chữ" là nội dung chứng từ theo mẫu
 * 01-TT/02-TT, luôn tiếng Việt, không qua i18n.
 *
 * ⚠ ĐỌC TỪ CHUỖI, KHÔNG QUA float (§2): làm tròn nửa lên về đồng rồi cắt chữ số thành nhóm ba.
 * ⚠ TỪ MỘT NGHÌN TỶ TRỞ LÊN bản cũ đọc sai ("Mộtundefined đồng" · "Một nghìn tỷ năm trăm tỷ"). Ở
 *   đây đọc theo khối chín chữ số, mỗi khối khác 0 mang thêm một chữ "tỷ": 1.500.000.000.000 là
 *   "Một nghìn năm trăm tỷ đồng".
 */
const WORD_DIGITS = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'] as const

/** Một nhóm ba chữ số. `full` = đã có nhóm đứng trước ⇒ đọc cả "không trăm" và "lẻ". */
const readTriple = (n: number, full: boolean): string => {
  const hundreds = Math.floor(n / 100)
  const tens = Math.floor((n % 100) / 10)
  const ones = n % 10
  const word = (d: number): string => WORD_DIGITS[d] as string
  let s = ''
  if (full || hundreds > 0) s += `${word(hundreds)} trăm`
  if (tens === 0) {
    if (ones > 0) s += `${s === '' ? '' : ' lẻ '}${word(ones)}`
  } else if (tens === 1) {
    s += ' mười'
    if (ones === 5) s += ' lăm'
    else if (ones > 0) s += ` ${word(ones)}`
  } else {
    s += ` ${word(tens)} mươi`
    if (ones === 1) s += ' mốt'
    else if (ones === 5) s += ' lăm'
    else if (ones > 0) s += ` ${word(ones)}`
  }
  return s.trim()
}

export const moneyInWordsVi = (v: string | number): string => {
  const whole = dec(v).abs().toDecimalPlaces(0, D.ROUND_HALF_UP).toFixed(0)
  if (/^0+$/.test(whole)) return 'Không đồng'
  // Khối chín chữ số từ PHẢI sang, rồi mỗi khối cắt tiếp thành ba nhóm ba chữ số.
  const padded = whole.padStart(Math.ceil(whole.length / 9) * 9, '0')
  const blocks: string[] = []
  for (let i = 0; i < padded.length; i += 9) blocks.push(padded.slice(i, i + 9))
  const parts: string[] = []
  let started = false
  blocks.forEach((block, blockIndex) => {
    const billions = blocks.length - 1 - blockIndex
    const blockParts: string[] = []
    for (let g = 0; g < 3; g++) {
      const n = Number(block.slice(g * 3, g * 3 + 3))
      if (n === 0) continue
      blockParts.push(`${readTriple(n, started)}${[' triệu', ' nghìn', ''][g] as string}`)
      started = true
    }
    if (blockParts.length > 0) parts.push(`${blockParts.join(' ')}${' tỷ'.repeat(billions)}`)
  })
  const out = `${parts.join(' ')} đồng`.replace(/\s+/g, ' ').trim()
  return out.charAt(0).toUpperCase() + out.slice(1)
}
